
import Vue from 'vue';
import BaseMap from '@/components/Map/BaseMap.vue';
import Component from 'vue-class-component';
import { getModule } from 'vuex-module-decorators';
import MachineStore from '@/store/modules/machine';
import { MachineKey } from '@/flightpath-components/models/MachineKey';
import MenuStore from '@/store/modules/menu';
import MissionStore from '@/store/modules/missions';
import router from '@/router';
import { DeployedSinglePathMission } from '@/flightpath-components/models/DeployedSinglePathMission';
import { Guid } from 'guid-typescript';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
import { Inject, Mixins } from 'vue-property-decorator';
import { Container } from 'inversify';
import SERVICE_IDENTIFIER from '@/flightpath-components/services/identifiers';
import { DeployedSinglePathMissionCloudState } from '@/flightpath-components/models/enums/DeployedSinglePathMissionCloudState';
import Auth, { CognitoUser } from '@aws-amplify/auth';
import { ImplementKeys, Parameters } from '@/services/Parameters';
import MissionToolTypes from '@/services/MissionToolTypes';
import AlarmStore from '@/store/modules/alarms';
import { AlarmSeverity } from '@/flightpath-components/models/AlarmModel';
import DurationFinder from '@/utils/DurationFinder';
import GlobalStore from '@/store/modules/global';
import MachineUpdateMixIn from '@/mixins/softwareUpdate';
import { h2Account } from '@/accounts';
import { HydrogenStorageState } from '@/models/ros-application/HydrogenSystemInfo';

// Define the component in class-style
@Component({ name: 'Home', components: { BaseMap } })
export default class Home extends Mixins(MachineUpdateMixIn) {
  public globalStore = getModule(GlobalStore, this.$store);
  public machineStore = getModule(MachineStore, this.$store);
  public alarmStore = getModule(AlarmStore, this.$store);
  public menuStore = getModule(MenuStore, this.$store);
  public missionStore = getModule(MissionStore, this.$store);
  private flightPathService: FlightPathServiceInterface | undefined = undefined;
  storageStates = HydrogenStorageState;

  @Inject('container')
  private container!: Container;
  user: CognitoUser | undefined;
  errorMessage: string | null = null;
  errorExists = false;
  loading = false;
  panel = [0];
  windowSize = {
    x: 0,
    y: 0
  };

  closeMission() {
    this.missionStore.selectMission(null);
  }
  get menuActive() {
    return this.menuStore.currentMenuState == 'Select-Mission';
  }

  get isH2Machine(): boolean {
    return this.globalStore.accountId === h2Account;
  }
  async launch() {
    if (this.missionStore.mission == null) return;
    this.errorExists = false;
    this.errorMessage = null;
    this.loading = true;
    const user = await Auth.currentAuthenticatedUser();
    const newMission = this.missionStore.mission as DeployedSinglePathMission;
    newMission.MissionDeploymentID = Guid.create().toString();
    newMission.TargetMachine = this.machineStore.selectedMachine as MachineKey;
    newMission.JobID = Guid.createEmpty().toString();
    newMission.DeployedTimeUTC = new Date();
    newMission.LastModifiedUTC = new Date();
    newMission.MissionState = DeployedSinglePathMissionCloudState.LaunchRequest;
    newMission.DeployerUserID = user.attributes.sub;

    try {
      this.missionStore.setDeployedMission(newMission);
      router.push({ name: 'MissionMonitoring' });
    } catch (error) {
      console.error('Error Deploying Mission', error);
      this.errorExists = true;
      this.errorMessage = 'Error Deploying Mission';
      this.missionStore.setDeployedMission(null);
    } finally {
      this.loading = false;
    }
  }
  created() {
    this.flightPathService = this.container.get<FlightPathServiceInterface>(SERVICE_IDENTIFIER.FLIGHT_PATH);
  }
  mounted() {
    this.onResize();

    Auth.currentAuthenticatedUser()
      .then((user) => {
        this.user = user;
      })
      .catch(() => (this.user = undefined));
  }
  containerWidth() {
    return this.menuStore.drawer ? this.windowSize.x - 310 : this.windowSize.x - 80;
  }
  onResize() {
    this.windowSize = { x: window.innerWidth, y: window.innerHeight };
  }
  get selectedItem(): number | null {
    return this.machineStore.selectedMachineIndex;
  }
  set selectedItem(value: number | null) {
    if (value != null && value == this.machineStore.selectedMachineIndex) this.machineStore.setSelectedMachineIndex(null);
    else this.machineStore.setSelectedMachineIndex(value as number);
  }
  selectMachine(value: MachineKey | null) {
    if (value != null && this.machineStore.selectedMachine == value) this.machineStore.setSelectedMachine(null);
    else this.machineStore.setSelectedMachine(value);
  }

  get currentAlarms(): number {
    if (this.machineStore.selectedMachine == null) return 0;
    return this.alarmStore.alarms.filter((o) => o.MachineId == this.machineStore.selectedMachine?.machineId && o.Severity == AlarmSeverity.Error).length;
  }
  editMission() {
    this.missionStore.editMission(true);
  }

  get isSprayingMission(): boolean {
    return (this.missionStore.applicationParameters.get(Parameters.implementType) as string) == ImplementKeys.sprayer;
  }

  get missionType() {
    if ((this.missionStore.applicationParameters.get(Parameters.implementType) as string) == ImplementKeys.sprayer) {
      return this.$t('views.missions.spraying_mission');
    } else {
      return this.$t('views.missions.tillage');
    }
  }

  get targetSpeed() {
    if (this.missionStore.mission == null || this.missionStore.mission.TargetSpeedKph == 0) return 0;
    return this.missionStore.mission.TargetSpeedKph;
  }

  get engineSpeed() {
    if (!this.missionStore.mission?.ApplicationParameters?.length) return 0;
    try {
      return JSON.parse(this.missionStore.mission.ApplicationParameters).engine_speed ?? 0;
    } catch (e) {
      return 0;
    }
  }

  get toolType() {
    const implementKey = this.missionStore.applicationParameters.get(Parameters.implementType) as string;
    return MissionToolTypes.toolTypes.filter((o) => o.value != ImplementKeys.weeder).find((x) => x.value == implementKey)?.text ?? '';
  }

  get estimatedDurationHours() {
    if (this.missionStore.mission == null || this.missionStore.mission.TargetSpeedKph == 0) return '0 h 0 min';

    return DurationFinder.findEstimatedMissionDurationHoursMinutes(
      this.missionStore.mission.Path.map((x) => new window.google.maps.LatLng(x.Lat, x.Long)),
      this.missionStore.mission.TargetSpeedKph
    );
  }
}
